import { memo, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationControls } from "@/components/ui/PaginationControls/PaginationControls";
import type { DistributionItem } from "@/lib/utils";

interface ReportTableProps {
  title: string;
  data: DistributionItem[];
}

export const ReportTable = memo(function ReportTable({
  title,
  data,
}: ReportTableProps) {
  const PAGE_SIZE = 15;
  const [page, setPage] = useState(1);

  const totalPages = useMemo(
    () => (data.length > 0 ? Math.ceil(data.length / PAGE_SIZE) : 1),
    [data.length],
  );

  const effectivePage = Math.min(page, totalPages);
  const startIndex = (effectivePage - 1) * PAGE_SIZE;

  const pageData = useMemo(
    () => data.slice(startIndex, startIndex + PAGE_SIZE),
    [data, startIndex, PAGE_SIZE],
  );

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="border-b border-border bg-muted/50 px-6 py-4">
        <h3 className="font-semibold text-foreground">{title}</h3>
      </div>

      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="h-9 w-[50px]">#</TableHead>
            <TableHead className="h-9">Label</TableHead>
            <TableHead className="h-9 w-[150px] text-right">
              Domains Count
            </TableHead>
            <TableHead className="h-9 w-[200px] text-right">
              Percentage
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={4}
                className="h-24 text-center text-muted-foreground"
              >
                No data available
              </TableCell>
            </TableRow>
          ) : (
            pageData.map((item, index) => (
              <TableRow key={item.label} className="hover:bg-muted/40">
                <TableCell className="py-2 font-medium text-muted-foreground tabular-nums">
                  {startIndex + index + 1}
                </TableCell>
                <TableCell className="py-2 font-medium text-foreground/80">
                  <span
                    className="block max-w-[260px] truncate"
                    title={item.label}
                  >
                    {item.label}
                  </span>
                </TableCell>
                <TableCell className="py-2 text-right font-bold text-foreground tabular-nums">
                  {item.count}
                </TableCell>
                <TableCell className="py-2 text-right text-muted-foreground">
                  <div className="flex items-center justify-end gap-3">
                    <span className="w-[45px] text-xs font-medium tabular-nums">
                      {item.percentage}%
                    </span>
                    <div className="h-2 w-24 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-[var(--chart-1)] transition-all duration-500 ease-out"
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {data.length > 0 && (
        <div className="flex items-center justify-between border-t border-border bg-muted/50 px-6 py-3">
          <span className="text-xs text-muted-foreground tabular-nums">
            Showing {startIndex + 1}-
            {Math.min(startIndex + PAGE_SIZE, data.length)} of {data.length}
          </span>
          <PaginationControls
            currentPage={effectivePage}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
});
